import { useEffect, useRef } from 'react';

export default function BirthdayScene() {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let w = 1, h = 1, dpr = 1, raf = 0, dead = false, t0 = performance.now();
    const rand = (a: number, b: number) => a + Math.random() * (b - a);
    const COLORS = ['#ff4081', '#7c4dff', '#18ffff', '#ffd740', '#69f0ae', '#ff6e40', '#ffffff', '#ff5cf0'];
    const pick = () => COLORS[(Math.random() * COLORS.length) | 0];

    let px = -9999, py = -9999, lastTrail = 0; // px<0 = pointer off canvas

    type P = { x: number; y: number; tx: number; ty: number; vx: number; vy: number; color: string; size: number; ph: number; arrived: boolean };
    let particles: P[] = [];

    const buildText = () => {
      const fontPx = Math.max(40, Math.min(Math.floor(w * 0.16), Math.floor(h * 0.27), 240));
      const off = document.createElement('canvas');
      off.width = w; off.height = h;
      const g = off.getContext('2d')!;
      g.fillStyle = '#fff';
      g.font = `900 ${fontPx}px Inter, "Segoe UI", system-ui, sans-serif`;
      g.textAlign = 'center';
      g.textBaseline = 'middle';
      const cy = h * 0.33;
      g.fillText('HAPPY', w / 2, cy - fontPx * 0.58);
      g.fillText('BIRTHDAY', w / 2, cy + fontPx * 0.58);
      const data = g.getImageData(0, 0, w, h).data;
      const step = Math.max(5, Math.round(fontPx / 19));
      const pts: { x: number; y: number }[] = [];
      for (let y = 0; y < h; y += step) for (let x = 0; x < w; x += step) {
        if (data[(y * w + x) * 4 + 3] > 128) pts.push({ x, y });
      }
      for (let i = pts.length - 1; i > 0; i--) { const j = (Math.random() * (i + 1)) | 0; [pts[i], pts[j]] = [pts[j], pts[i]]; }
      const targets = pts.slice(0, 2600);
      particles = targets.map((p) => ({
        x: rand(0, w), y: rand(0, h), tx: p.x, ty: p.y, vx: 0, vy: 0,
        color: pick(), size: rand(1.7, 3.4), ph: rand(0, Math.PI * 2), arrived: false,
      }));
    };

    const resize = () => {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth; h = canvas.clientHeight;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      buildText();
    };
    resize();
    window.addEventListener('resize', resize);

    type Rocket = { x: number; y: number; vx: number; vy: number; targetY: number; color: string; trail: { x: number; y: number }[] };
    type Spark = { x: number; y: number; vx: number; vy: number; life: number; decay: number; color: string; size: number };
    const rockets: Rocket[] = [];
    const sparks: Spark[] = [];
    const launchTo = (tx: number, ty: number) => rockets.push({ x: tx, y: h + 10, vx: rand(-0.4, 0.4), vy: rand(-12, -9.5), targetY: ty, color: pick(), trail: [] });
    const launch = () => launchTo(rand(w * 0.1, w * 0.9), rand(h * 0.1, h * 0.55));
    const explode = (x: number, y: number, color: string) => {
      sparks.push({ x, y, vx: 0, vy: 0, life: 1, decay: 0.05, color: '#fff', size: 11 });
      const n = (rand(70, 120)) | 0;
      const base = rand(0, Math.PI * 2);
      for (let i = 0; i < n; i++) {
        const a = base + (i / n) * Math.PI * 2 + rand(-0.1, 0.1);
        const sp = rand(1.4, 6.8);
        sparks.push({ x, y, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp, life: 1, decay: rand(0.008, 0.018), color: Math.random() < 0.22 ? '#fff' : color, size: rand(1.1, 2.9) });
      }
    };

    const toLocal = (e: PointerEvent | MouseEvent) => {
      const r = canvas.getBoundingClientRect();
      px = e.clientX - r.left; py = e.clientY - r.top;
    };
    const onMove = (e: PointerEvent) => {
      toLocal(e);
      const now = performance.now();
      if (now - lastTrail > 16) {
        lastTrail = now;
        for (let i = 0; i < 2; i++) sparks.push({ x: px, y: py, vx: rand(-0.6, 0.6), vy: rand(-0.6, 0.2), life: 1, decay: rand(0.03, 0.05), color: pick(), size: rand(1.2, 2.4) });
      }
    };
    const onLeave = () => { px = -9999; py = -9999; };
    const onDown = (e: MouseEvent) => { toLocal(e); explode(px, py, pick()); };
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerleave', onLeave);
    canvas.addEventListener('pointerdown', onDown);

    const REPEL = 110, REPEL2 = REPEL * REPEL;
    let lastLaunch = 0;
    const step = (now: number) => {
      if (dead) return;
      const settle = Math.min(1, (now - t0) / 1600);

      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(12, 8, 26, 0.22)';
      ctx.fillRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';

      for (const p of particles) {
        const k = 0.045 + 0.06 * settle;
        p.vx = (p.vx + (p.tx - p.x) * k) * 0.82;
        p.vy = (p.vy + (p.ty - p.y) * k) * 0.82;
        const dx = p.x - px, dy = p.y - py, d2 = dx * dx + dy * dy;
        if (d2 < REPEL2) { const d = Math.sqrt(d2) || 1, f = (1 - d / REPEL) * 4.2; p.vx += (dx / d) * f; p.vy += (dy / d) * f; }
        p.x += p.vx; p.y += p.vy;
        if (!p.arrived && Math.abs(p.tx - p.x) < 1.2 && Math.abs(p.ty - p.y) < 1.2) p.arrived = true;
        const tw = p.arrived ? 0.6 + 0.4 * Math.sin(now * 0.004 + p.ph) : 1;
        if (p.arrived) { p.x += Math.sin(now * 0.002 + p.ph) * 0.15; p.y += Math.cos(now * 0.0023 + p.ph) * 0.15; }
        ctx.globalAlpha = tw;
        ctx.fillStyle = p.color;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalAlpha = 1;

      if (now - lastLaunch > rand(300, 620)) { launch(); if (Math.random() < 0.4) launch(); lastLaunch = now; }
      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.vy += 0.12; r.x += r.vx; r.y += r.vy;
        r.trail.push({ x: r.x, y: r.y }); if (r.trail.length > 8) r.trail.shift();
        ctx.strokeStyle = r.color; ctx.lineWidth = 2; ctx.beginPath();
        for (let ti = 0; ti < r.trail.length; ti++) { const pt = r.trail[ti]; ti === 0 ? ctx.moveTo(pt.x, pt.y) : ctx.lineTo(pt.x, pt.y); }
        ctx.stroke();
        if (r.vy >= -0.5 || r.y <= r.targetY) { explode(r.x, r.y, r.color); rockets.splice(i, 1); }
      }
      for (let i = sparks.length - 1; i >= 0; i--) {
        const s = sparks[i];
        s.vx *= 0.985; s.vy = s.vy * 0.985 + 0.05; s.x += s.vx; s.y += s.vy; s.life -= s.decay;
        if (s.life <= 0) { sparks.splice(i, 1); continue; }
        ctx.globalAlpha = Math.max(0, s.life);
        ctx.fillStyle = s.color;
        ctx.beginPath(); ctx.arc(s.x, s.y, s.size * (0.4 + s.life * 0.6), 0, Math.PI * 2); ctx.fill();
      }
      ctx.globalAlpha = 1;
      raf = requestAnimationFrame(step);
    };

    launch(); launch(); launch();
    raf = requestAnimationFrame(step);

    return () => {
      dead = true;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerleave', onLeave);
      canvas.removeEventListener('pointerdown', onDown);
    };
  }, []);

  return <canvas ref={ref} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }} />;
}
